import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { SupplierDirectorySort } from 'models/supplier-request';
import { DirectoryBrowserConfig } from './directory-browser-config';
import DirectoryCountrySelect from './directory-country-select.component';

export type SupplierBrowserFilterState = {
    searchText: string;
    services: string[];
    sort: SupplierDirectorySort;
    /** 0 = any distance. */
    maxDistanceKm: number;
    hideConnected: boolean;
    /** DD-3698: ISO alpha-2; empty = all countries. */
    country: string;
};

type SupplierBrowserFiltersProps = {
    filters: SupplierBrowserFilterState;
    onChange: (changes: Partial<SupplierBrowserFilterState>) => void;
    /** Distance sort and filter need the browsing lab's own location; hidden without it. */
    canUseDistance: boolean;
    /** DD-3696: the pills and wording of the directory being browsed. */
    config: DirectoryBrowserConfig;
    /** Right-aligned at the end of the first row, above the pills (count, map button). */
    actions?: ReactNode;
};

const DISTANCE_OPTIONS_KM = [25, 50, 100, 250];
const WITHIN_PREFIX = 'WITHIN_';

/** The dropdown's value: by name, nearest first, or nearest first within a radius. */
const proximityOf = (filters: SupplierBrowserFilterState) => {
    if (filters.sort === SupplierDirectorySort.NAME) return SupplierDirectorySort.NAME;
    return filters.maxDistanceKm ? `${WITHIN_PREFIX}${filters.maxDistanceKm}` : SupplierDirectorySort.DISTANCE;
};

const filtersOfProximity = (value: string): Partial<SupplierBrowserFilterState> => {
    if (value === SupplierDirectorySort.NAME) return { sort: SupplierDirectorySort.NAME, maxDistanceKm: 0 };
    const km = value.startsWith(WITHIN_PREFIX) ? Number(value.slice(WITHIN_PREFIX.length)) : 0;
    return { sort: SupplierDirectorySort.DISTANCE, maxDistanceKm: km };
};

/** DD-3662: search, services, order/distance and "hide connected" for the directory browser. */
const SupplierBrowserFilters: FC<SupplierBrowserFiltersProps> = ({
    filters,
    onChange,
    canUseDistance,
    config,
    actions,
}) => {
    const { t: translate } = useTranslation(['client-directory', 'lab-settings']);

    const toggleService = (service: string) =>
        onChange({
            services: filters.services.includes(service)
                ? filters.services.filter((chosen) => chosen !== service)
                : [...filters.services, service],
        });

    return (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
            <TextField
                size="small"
                // Wider where there is no "hide connected" checkbox beside it (Find customer).
                sx={{ minWidth: config.hideConnectedFilter === false ? 520 : 420 }}
                placeholder={translate('client-directory:SEARCH_SUPPLIERS')}
                value={filters.searchText}
                onChange={(event) => onChange({ searchText: event.target.value })}
                inputProps={{
                    'aria-label': translate('client-directory:SEARCH_SUPPLIERS'),
                    'data-test': 'supplier-browser.search',
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />
            {config.countryFilter ? (
                <DirectoryCountrySelect value={filters.country} onChange={(country) => onChange({ country })} />
            ) : undefined}
            {/* DD-3695: ten pills on a row of their own; several chosen = labs offering any of them. */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} width="100%" order={1}>
                <Typography variant="body2" color="text.secondary">
                    {translate(config.texts.servicesFilter ?? 'client-directory:SUPPLIER_SERVICES_FILTER')}
                </Typography>
                {config.services.map((service) => {
                    const isChosen = filters.services.includes(service);
                    return (
                        <Tooltip
                            key={service}
                            describeChild
                            title={config.serviceTooltipKey ? translate(config.serviceTooltipKey(service)) : ''}
                        >
                            <Chip
                                label={translate(config.serviceLabelKey(service))}
                                color={isChosen ? 'primary' : 'default'}
                                variant={isChosen ? 'filled' : 'outlined'}
                                onClick={() => toggleService(service)}
                                aria-pressed={isChosen}
                                data-test={`supplier-browser.filter-${service}`}
                            />
                        </Tooltip>
                    );
                })}
            </Box>
            {/* DD-3674: one dropdown for order and distance, only when the lab has a location. */}
            {canUseDistance ? (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="supplier-browser-sort">{translate('client-directory:SORT_SUPPLIERS')}</InputLabel>
                    <Select
                        labelId="supplier-browser-sort"
                        label={translate('client-directory:SORT_SUPPLIERS')}
                        value={proximityOf(filters)}
                        onChange={(event) => onChange(filtersOfProximity(event.target.value))}
                        data-test="supplier-browser.sort"
                    >
                        <MenuItem value={SupplierDirectorySort.NAME}>
                            {translate('client-directory:SORT_SUPPLIERS_BY_NAME')}
                        </MenuItem>
                        <MenuItem value={SupplierDirectorySort.DISTANCE}>
                            {translate('client-directory:SORT_SUPPLIERS_BY_DISTANCE')}
                        </MenuItem>
                        {DISTANCE_OPTIONS_KM.map((km) => (
                            <MenuItem key={km} value={`${WITHIN_PREFIX}${km}`}>
                                {translate('client-directory:WITHIN_KM', { km })}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : undefined}
            {config.hideConnectedFilter !== false ? (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.hideConnected}
                            onChange={(event) => onChange({ hideConnected: event.target.checked })}
                            data-test="supplier-browser.hide-connected"
                        />
                    }
                    label={translate(config.texts.hideConnected ?? '')}
                />
            ) : undefined}
            {actions ? (
                <Box display="flex" alignItems="center" gap={2} ml="auto">
                    {actions}
                </Box>
            ) : undefined}
        </Box>
    );
};

export default SupplierBrowserFilters;
